/**
 * MarkerListEditor — Chế độ "Sửa nhanh" dạng bảng cho markers của 1 bản đồ giá.
 *
 * Mục đích: cập nhật giá hàng loạt nhanh (tên / giá min / giá max / toạ độ)
 * thay vì phải click từng marker trên bản đồ rồi mở form.
 *
 * - Sửa inline trực tiếp trong ô input.
 * - Lưu từng dòng (nút Lưu hiện khi dòng có thay đổi) hoặc "Lưu tất cả".
 * - Giá min/max -> tự sinh price_text "X-Y tr/m²" + price_value=min (number) khớp
 *   logic form marker hiện có.
 * - Có ô tìm kiếm lọc theo tên/địa chỉ.
 *
 * props:
 *  - mapId
 *  - markers: mảng marker hiện tại
 *  - onClose: () => void
 *  - onSaved: () => void   (gọi để parent reload markers)
 */
import { useState, useMemo, useEffect } from 'react'
import {
  HiOutlineX, HiOutlineSearch, HiOutlineCheck, HiOutlineLocationMarker,
  HiOutlinePlus, HiOutlineTrash,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import marketMapService from '../../services/marketMapService'

// Parse "30-45 tr/m²" / "30 tr/m²" -> { min:'30', max:'45' }
function parsePrice(text) {
  if (!text) return { min: '', max: '' }
  const nums = String(text).match(/[\d.]+/g)
  if (!nums || !nums.length) return { min: '', max: '' }
  return { min: nums[0] || '', max: nums[1] || '' }
}

// min/max -> price_text giống logic trong form marker
function buildPriceText(min, max) {
  if (min && max) return `${min}-${max} tr/m²`
  if (min) return `${min} tr/m²`
  return ''
}

function toRow(m) {
  const { min, max } = parsePrice(m.price_text)
  return {
    id: m.id,
    name: m.name || '',
    price_min: min,
    price_max: max,
    latitude: m.latitude ?? '',
    longitude: m.longitude ?? '',
    _orig: { name: m.name || '', min, max, lat: String(m.latitude ?? ''), lng: String(m.longitude ?? '') },
  }
}

const emptyNewRow = () => ({
  _key: Math.random().toString(36).slice(2),
  name: '', price_min: '', price_max: '', latitude: '', longitude: '',
})

export default function MarkerListEditor({ mapId, markers = [], onClose, onSaved }) {
  const [rows, setRows] = useState(() => markers.map(toRow))
  const [search, setSearch] = useState('')
  const [savingId, setSavingId] = useState(null)
  const [savingAll, setSavingAll] = useState(false)
  // các dòng marker MỚI cần thêm (mặc định 1 dòng trống)
  const [newRows, setNewRows] = useState(() => [emptyNewRow()])
  const [addingAll, setAddingAll] = useState(false)

  useEffect(() => { setRows(markers.map(toRow)) }, [markers])

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const q = search.trim().toLowerCase()
    return rows.filter((r) => r.name.toLowerCase().includes(q))
  }, [rows, search])

  const isDirty = (r) =>
    r.name !== r._orig.name ||
    r.price_min !== r._orig.min ||
    r.price_max !== r._orig.max ||
    String(r.latitude) !== r._orig.lat ||
    String(r.longitude) !== r._orig.lng

  const setField = (id, key, val) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: val } : r)))

  const buildPayload = (r) => {
    const price_text = buildPriceText(r.price_min, r.price_max)
    return {
      name: r.name,
      price_text,
      price_value: r.price_min !== '' ? Number(r.price_min) : null,
      latitude: r.latitude !== '' ? Number(r.latitude) : undefined,
      longitude: r.longitude !== '' ? Number(r.longitude) : undefined,
    }
  }

  const saveRow = async (r) => {
    if (!r.name.trim()) { toast.error('Tên không được trống'); return false }
    setSavingId(r.id)
    try {
      await marketMapService.updateMarker(mapId, r.id, buildPayload(r))
      // cập nhật _orig để dòng hết "dirty"
      setRows((prev) => prev.map((x) => x.id === r.id
        ? { ...x, _orig: { name: x.name, min: x.price_min, max: x.price_max, lat: String(x.latitude), lng: String(x.longitude) } }
        : x))
      return true
    } catch {
      return false  // interceptor đã toast
    } finally {
      setSavingId(null)
    }
  }

  const handleSaveRow = async (r) => {
    if (await saveRow(r)) { toast.success('Đã lưu'); onSaved?.() }
  }

  const handleSaveAll = async () => {
    const dirty = rows.filter(isDirty)
    if (!dirty.length) { toast('Không có thay đổi nào', { icon: 'ℹ️' }); return }
    setSavingAll(true)
    let ok = 0
    for (const r of dirty) {
      // eslint-disable-next-line no-await-in-loop
      if (await saveRow(r)) ok++
    }
    setSavingAll(false)
    toast.success(`Đã lưu ${ok}/${dirty.length} dòng`)
    onSaved?.()
  }

  const dirtyCount = rows.filter(isDirty).length

  // ---- Thêm marker mới ----
  const setNewField = (key, field, val) =>
    setNewRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: val } : r)))

  const addEmptyNewRow = () => setNewRows((prev) => [...prev, emptyNewRow()])

  const removeNewRow = (key) =>
    setNewRows((prev) => (prev.length > 1 ? prev.filter((r) => r._key !== key) : [emptyNewRow()]))

  const newRowValid = (r) =>
    r.name.trim() && r.latitude !== '' && r.longitude !== '' &&
    !Number.isNaN(Number(r.latitude)) && !Number.isNaN(Number(r.longitude))

  const createOne = async (r) => {
    const payload = {
      name: r.name.trim(),
      latitude: Number(r.latitude),
      longitude: Number(r.longitude),
      price_text: buildPriceText(r.price_min, r.price_max),
      price_value: r.price_min !== '' ? Number(r.price_min) : null,
      // các trường còn lại để backend tự điền mặc định (màu, icon, đơn vị, is_active...)
    }
    await marketMapService.createMarker(mapId, payload)
  }

  const handleAddAll = async () => {
    const valid = newRows.filter(newRowValid)
    if (!valid.length) {
      toast.error('Cần ít nhất Tên + Vĩ độ + Kinh độ cho marker mới')
      return
    }
    setAddingAll(true)
    let ok = 0
    for (const r of valid) {
      // eslint-disable-next-line no-await-in-loop
      try { await createOne(r); ok++ } catch { /* interceptor */ }
    }
    setAddingAll(false)
    toast.success(`Đã thêm ${ok}/${valid.length} marker mới`)
    setNewRows([emptyNewRow()])  // reset
    onSaved?.()                  // reload danh sách
  }

  const newValidCount = newRows.filter(newRowValid).length

  return (
    <div className="fixed inset-0 z-[600] bg-black/40 flex items-start sm:items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">Sửa nhanh giá (dạng danh sách)</h3>
            <p className="text-xs text-gray-500">{rows.length} marker · sửa giá min/max, toạ độ rồi lưu nhanh</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
          <div className="relative flex-1 max-w-xs">
            <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên marker..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-400 outline-none"
            />
          </div>
          <div className="flex-1" />
          <button
            onClick={handleSaveAll}
            disabled={savingAll || dirtyCount === 0}
            className="btn-primary btn-sm disabled:opacity-50"
          >
            {savingAll ? <span className="spinner w-4 h-4 mr-1" /> : <HiOutlineCheck className="w-4 h-4 mr-1" />}
            Lưu tất cả{dirtyCount ? ` (${dirtyCount})` : ''}
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              {rows.length === 0 ? 'Bản đồ chưa có marker nào.' : 'Không có marker khớp tìm kiếm.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-gray-600">
                  <th className="px-3 py-2 font-medium w-8">#</th>
                  <th className="px-3 py-2 font-medium min-w-[180px]">Tên marker</th>
                  <th className="px-3 py-2 font-medium w-24">Giá min</th>
                  <th className="px-3 py-2 font-medium w-24">Giá max</th>
                  <th className="px-3 py-2 font-medium w-28">Vĩ độ (lat)</th>
                  <th className="px-3 py-2 font-medium w-28">Kinh độ (lng)</th>
                  <th className="px-3 py-2 font-medium w-20 text-right">Lưu</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const dirty = isDirty(r)
                  return (
                    <tr key={r.id} className={dirty ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-1.5 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-1.5">
                        <input
                          value={r.name}
                          onChange={(e) => setField(r.id, 'name', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-2 focus:ring-primary-400 outline-none"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number" inputMode="decimal" value={r.price_min}
                          onChange={(e) => setField(r.id, 'price_min', e.target.value)}
                          placeholder="tr/m²"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-primary-400 outline-none"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number" inputMode="decimal" value={r.price_max}
                          onChange={(e) => setField(r.id, 'price_max', e.target.value)}
                          placeholder="tr/m²"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-primary-400 outline-none"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number" inputMode="decimal" value={r.latitude}
                          onChange={(e) => setField(r.id, 'latitude', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-right text-xs focus:ring-2 focus:ring-primary-400 outline-none"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number" inputMode="decimal" value={r.longitude}
                          onChange={(e) => setField(r.id, 'longitude', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-right text-xs focus:ring-2 focus:ring-primary-400 outline-none"
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <button
                          onClick={() => handleSaveRow(r)}
                          disabled={!dirty || savingId === r.id}
                          className={dirty
                            ? 'inline-flex items-center px-2 py-1 rounded bg-primary-600 text-white text-xs hover:bg-primary-700 disabled:opacity-50'
                            : 'inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-400 text-xs cursor-default'}
                          title={dirty ? 'Lưu dòng này' : 'Chưa thay đổi'}
                        >
                          {savingId === r.id ? <span className="spinner w-3.5 h-3.5" /> : <HiOutlineCheck className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Thêm marker mới (chỉ Tên/Min/Max/Lat/Lng, còn lại để mặc định) */}
        <div className="border-t border-gray-200 bg-gray-50/60">
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <HiOutlinePlus className="w-4 h-4 text-emerald-600" /> Thêm marker mới
            </div>
            <span className="text-2xs text-gray-400">Chỉ cần Tên + toạ độ; màu/icon/đơn vị tự mặc định</span>
          </div>
          <div className="px-4 pb-3 max-h-44 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {newRows.map((r) => {
                  const valid = newRowValid(r)
                  return (
                    <tr key={r._key}>
                      <td className="py-1 pr-2 w-8 text-gray-300">＋</td>
                      <td className="py-1 pr-2 min-w-[180px]">
                        <input
                          value={r.name}
                          onChange={(e) => setNewField(r._key, 'name', e.target.value)}
                          placeholder="Tên marker *"
                          className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-2 focus:ring-emerald-400 outline-none"
                        />
                      </td>
                      <td className="py-1 pr-2 w-24">
                        <input type="number" inputMode="decimal" value={r.price_min}
                          onChange={(e) => setNewField(r._key, 'price_min', e.target.value)}
                          placeholder="Min" className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-emerald-400 outline-none" />
                      </td>
                      <td className="py-1 pr-2 w-24">
                        <input type="number" inputMode="decimal" value={r.price_max}
                          onChange={(e) => setNewField(r._key, 'price_max', e.target.value)}
                          placeholder="Max" className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-emerald-400 outline-none" />
                      </td>
                      <td className="py-1 pr-2 w-28">
                        <input type="number" inputMode="decimal" value={r.latitude}
                          onChange={(e) => setNewField(r._key, 'latitude', e.target.value)}
                          placeholder="lat *" className="w-full px-2 py-1 border border-gray-200 rounded text-right text-xs focus:ring-2 focus:ring-emerald-400 outline-none" />
                      </td>
                      <td className="py-1 pr-2 w-28">
                        <input type="number" inputMode="decimal" value={r.longitude}
                          onChange={(e) => setNewField(r._key, 'longitude', e.target.value)}
                          placeholder="lng *" className="w-full px-2 py-1 border border-gray-200 rounded text-right text-xs focus:ring-2 focus:ring-emerald-400 outline-none" />
                      </td>
                      <td className="py-1 w-20 text-right">
                        <span className={valid ? 'text-emerald-500' : 'text-gray-300'} title={valid ? 'Hợp lệ' : 'Cần Tên + toạ độ'}>
                          {valid ? <HiOutlineCheck className="w-4 h-4 inline" /> : null}
                        </span>
                        <button onClick={() => removeNewRow(r._key)} className="ml-1 text-rose-300 hover:text-rose-500" title="Bỏ dòng">
                          <HiOutlineTrash className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={addEmptyNewRow}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-white text-gray-600">
                <HiOutlinePlus className="w-4 h-4" /> Thêm dòng
              </button>
              <button onClick={handleAddAll} disabled={addingAll || newValidCount === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                {addingAll ? <span className="spinner w-4 h-4" /> : <HiOutlineCheck className="w-4 h-4" />}
                Lưu marker mới{newValidCount ? ` (${newValidCount})` : ''}
              </button>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 text-2xs text-gray-400 flex items-center gap-1.5">
          <HiOutlineLocationMarker className="w-3.5 h-3.5" />
          Dòng nền vàng = có thay đổi chưa lưu. Giá min/max tính theo triệu đồng/m². Để trống max nếu chỉ có 1 mức giá.
        </div>
      </div>
    </div>
  )
}
